/**
 * Rules page. Backed by the Zustand rule store. Lists optimization rules and
 * provides a create/edit modal ("rule editor"), an active toggle, and delete.
 * The editor lets the user scope a rule to an ad account and/or campaign and
 * configure its trigger, time condition, action and target.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import Card from '@/components/Cards/Card';
import Table, { type Column } from '@/components/Tables/Table';
import Button from '@/components/Forms/Button';
import Input from '@/components/Forms/Input';
import Select, { type SelectOption } from '@/components/Forms/Select';
import Badge from '@/components/common/Badge';
import Alert from '@/components/Alerts/Alert';
import Modal from '@/components/Modals/Modal';
import { useToast } from '@/components/Alerts/toastContext';
import { useRuleStore } from '@/store/ruleStore';
import * as adAccountService from '@/services/adAccountService';
import * as campaignService from '@/services/campaignService';
import type {
  AdAccountDTO,
  CampaignDTO,
  CreateRuleInput,
  OptimizationRuleDTO,
} from '@shared/types';
import { ActionType, TargetType, TriggerType } from '@shared/constants';
import {
  ACTION_TYPE_LABELS,
  ACTION_TYPE_OPTIONS,
  isPercentAction,
  TARGET_TYPE_LABELS,
  TARGET_TYPE_OPTIONS,
  TRIGGER_TYPE_LABELS,
  TRIGGER_TYPE_OPTIONS,
} from '@/pages/labels';
import { formatNumber } from '@/utils/formatters';
import { required } from '@/utils/validators';

/** Local form model (all strings for controlled inputs). */
interface RuleForm {
  name: string;
  description: string;
  adAccountId: string;
  campaignId: string;
  triggerType: TriggerType;
  triggerValue: string;
  hour: string;
  actionType: ActionType;
  actionValue: string;
  targetType: TargetType;
}

const EMPTY_FORM: RuleForm = {
  name: '',
  description: '',
  adAccountId: '',
  campaignId: '',
  triggerType: TriggerType.REVENUE_THRESHOLD,
  triggerValue: '',
  hour: '',
  actionType: ActionType.INCREASE_BUDGET,
  actionValue: '',
  targetType: TargetType.CAMPAIGN,
};

/** Build a form model from an existing rule DTO (for editing). */
function formFromRule(rule: OptimizationRuleDTO): RuleForm {
  return {
    name: rule.name,
    description: rule.description ?? '',
    adAccountId: rule.adAccountId ?? '',
    campaignId: rule.campaignId ?? '',
    triggerType: rule.triggerType,
    triggerValue: rule.triggerValue ?? '',
    hour: rule.timeCondition?.hour != null ? String(rule.timeCondition.hour) : '',
    actionType: rule.actionType,
    actionValue: rule.actionValue ?? '',
    targetType: rule.targetType,
  };
}

/** Rules management view. */
export default function Rules(): ReactElement {
  const toast = useToast();

  const rules = useRuleStore((s) => s.rules);
  const loading = useRuleStore((s) => s.loading);
  const error = useRuleStore((s) => s.error);
  const fetch = useRuleStore((s) => s.fetch);
  const create = useRuleStore((s) => s.create);
  const update = useRuleStore((s) => s.update);
  const remove = useRuleStore((s) => s.remove);
  const toggle = useRuleStore((s) => s.toggle);

  const [adAccounts, setAdAccounts] = useState<AdAccountDTO[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignDTO[]>([]);

  // Editor modal state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleForm>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof RuleForm, string>>>({});
  const [saving, setSaving] = useState(false);

  // Delete + toggle state.
  const [deleteTarget, setDeleteTarget] = useState<OptimizationRuleDTO | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadRules = useCallback(async (): Promise<void> => {
    try {
      await fetch();
    } catch {
      // Error captured in store.
    }
  }, [fetch]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  useEffect(() => {
    Promise.allSettled([adAccountService.list(), campaignService.list()]).then(
      ([adRes, campRes]) => {
        if (adRes.status === 'fulfilled') setAdAccounts(adRes.value);
        if (campRes.status === 'fulfilled') setCampaigns(campRes.value.campaigns);
      },
    );
  }, []);

  const openCreate = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setEditorOpen(true);
  };

  const openEdit = (rule: OptimizationRuleDTO): void => {
    setEditingId(rule.id);
    setForm(formFromRule(rule));
    setFormErrors({});
    setEditorOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof RuleForm, string>> = {};
    const nameErr = required(form.name);
    if (nameErr) errors.name = nameErr;
    if (form.triggerValue.trim() !== '' && !Number.isFinite(Number(form.triggerValue))) {
      errors.triggerValue = 'Giá trị không hợp lệ';
    }
    if (form.actionValue.trim() !== '' && !Number.isFinite(Number(form.actionValue))) {
      errors.actionValue = 'Giá trị không hợp lệ';
    }
    if (form.hour.trim() !== '') {
      const h = Number(form.hour);
      if (!Number.isInteger(h) || h < 0 || h > 23) errors.hour = 'Giờ phải từ 0 đến 23';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildInput = (): CreateRuleInput => ({
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    adAccountId: form.adAccountId || null,
    campaignId: form.campaignId || null,
    triggerType: form.triggerType,
    triggerValue: form.triggerValue.trim() === '' ? undefined : Number(form.triggerValue),
    timeCondition: form.hour.trim() === '' ? undefined : { hour: Number(form.hour) },
    actionType: form.actionType,
    actionValue: form.actionValue.trim() === '' ? undefined : Number(form.actionValue),
    targetType: form.targetType,
  });

  const handleSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const input = buildInput();
      if (editingId) {
        await update(editingId, input);
        toast.success('Đã cập nhật quy tắc');
      } else {
        await create(input);
        toast.success('Đã tạo quy tắc mới');
      }
      setEditorOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Lưu quy tắc thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule: OptimizationRuleDTO): Promise<void> => {
    setTogglingId(rule.id);
    try {
      await toggle(rule.id, !rule.isActive);
      toast.success(!rule.isActive ? 'Đã bật quy tắc' : 'Đã tắt quy tắc');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Thay đổi trạng thái thất bại');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await remove(deleteTarget.id);
      toast.success('Đã xóa quy tắc');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Xóa quy tắc thất bại');
    } finally {
      setDeleting(false);
    }
  };

  const adAccountOptions: SelectOption[] = useMemo(
    () => adAccounts.map((a) => ({ value: a.id, label: a.name ?? a.adAccountIdFb })),
    [adAccounts],
  );
  const campaignOptions: SelectOption[] = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.name ?? c.campaignIdFb })),
    [campaigns],
  );

  const percentAction = isPercentAction(form.actionType);

  const columns: Column<OptimizationRuleDTO>[] = [
    {
      key: 'name',
      header: 'Tên quy tắc',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.name}</p>
          {row.description && <p className="text-xs text-gray-500">{row.description}</p>}
        </div>
      ),
    },
    {
      key: 'trigger',
      header: 'Điều kiện',
      render: (row) => (
        <span>
          {TRIGGER_TYPE_LABELS[row.triggerType]}
          {row.triggerValue ? `: ${formatNumber(row.triggerValue)}` : ''}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Hành động',
      render: (row) => (
        <span>
          {ACTION_TYPE_LABELS[row.actionType]}
          {row.actionValue && isPercentAction(row.actionType) ? `: ${row.actionValue}%` : ''}
        </span>
      ),
    },
    {
      key: 'targetType',
      header: 'Đối tượng',
      render: (row) => TARGET_TYPE_LABELS[row.targetType],
    },
    {
      key: 'isActive',
      header: 'Trạng thái',
      render: (row) => (
        <Badge color={row.isActive ? 'green' : 'gray'}>
          {row.isActive ? 'Đang bật' : 'Đã tắt'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Hành động',
      align: 'right',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            loading={togglingId === row.id}
            onClick={() => void handleToggle(row)}
          >
            {row.isActive ? 'Tắt' : 'Bật'}
          </Button>
          <Button variant="secondary" onClick={() => openEdit(row)}>
            Sửa
          </Button>
          <Button variant="danger" onClick={() => setDeleteTarget(row)}>
            Xóa
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quy tắc tối ưu</h1>
          <p className="mt-1 text-sm text-gray-500">
            Thiết lập các quy tắc để AI Agent tự động tối ưu chiến dịch.
          </p>
        </div>
        <Button onClick={openCreate}>Tạo quy tắc mới</Button>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <Card>
        <Table
          columns={columns}
          rows={rules}
          loading={loading}
          rowKey={(row) => row.id}
          emptyMessage="Chưa có quy tắc nào. Hãy tạo quy tắc đầu tiên."
        />
      </Card>

      {/* Rule editor modal */}
      <Modal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editingId ? 'Chỉnh sửa quy tắc' : 'Tạo quy tắc mới'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>
              Hủy
            </Button>
            <Button type="submit" form="rule-form" loading={saving}>
              {editingId ? 'Cập nhật' : 'Tạo quy tắc'}
            </Button>
          </>
        }
      >
        <form id="rule-form" onSubmit={handleSave} noValidate className="space-y-4">
          <Input
            label="Tên quy tắc"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={formErrors.name}
            required
          />
          <Input
            label="Mô tả"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Mô tả ngắn gọn (tùy chọn)"
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Tài khoản quảng cáo"
              options={adAccountOptions}
              placeholder="Áp dụng tất cả"
              value={form.adAccountId}
              onChange={(e) => setForm((f) => ({ ...f, adAccountId: e.target.value }))}
            />
            <Select
              label="Chiến dịch"
              options={campaignOptions}
              placeholder="Áp dụng tất cả"
              value={form.campaignId}
              onChange={(e) => setForm((f) => ({ ...f, campaignId: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Loại điều kiện (Trigger)"
              options={TRIGGER_TYPE_OPTIONS}
              value={form.triggerType}
              onChange={(e) =>
                setForm((f) => ({ ...f, triggerType: e.target.value as TriggerType }))
              }
            />
            <Input
              label="Giá trị điều kiện"
              type="number"
              value={form.triggerValue}
              onChange={(e) => setForm((f) => ({ ...f, triggerValue: e.target.value }))}
              error={formErrors.triggerValue}
              placeholder="Ví dụ: 1500000"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Loại hành động"
              options={ACTION_TYPE_OPTIONS}
              value={form.actionType}
              onChange={(e) =>
                setForm((f) => ({ ...f, actionType: e.target.value as ActionType }))
              }
            />
            <Input
              label={percentAction ? 'Giá trị hành động (%)' : 'Giá trị hành động'}
              type="number"
              value={form.actionValue}
              onChange={(e) => setForm((f) => ({ ...f, actionValue: e.target.value }))}
              error={formErrors.actionValue}
              placeholder={percentAction ? 'Ví dụ: 20' : 'Không áp dụng'}
              disabled={!percentAction}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Đối tượng áp dụng"
              options={TARGET_TYPE_OPTIONS}
              value={form.targetType}
              onChange={(e) =>
                setForm((f) => ({ ...f, targetType: e.target.value as TargetType }))
              }
            />
            <Input
              label="Điều kiện thời gian (giờ trong ngày)"
              type="number"
              min={0}
              max={23}
              value={form.hour}
              onChange={(e) => setForm((f) => ({ ...f, hour: e.target.value }))}
              error={formErrors.hour}
              placeholder="0 - 23 (tùy chọn)"
            />
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Xác nhận xóa"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Hủy
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void handleDelete()}>
              Xóa
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Bạn có chắc muốn xóa quy tắc <span className="font-semibold">{deleteTarget?.name}</span>?
        </p>
      </Modal>
    </div>
  );
}
